// React
import React, { useEffect, useState } from 'react';

// Packages
import t from 'prop-types';
import Image from 'next/legacy/image';

// Utilities
import { getPermissionRules } from 'utilities';
import {
    useElseware,
    useLabels,
    usePermissions,
    useUser,
    useSearchAndFilter,
} from 'utilities/hooks';
import { useInitiativeDataStore } from 'utilities/store';
import { ACCOUNTS } from 'utilities/configuration/permission';
import { CONSTANTS } from 'utilities/configuration';

// Components
import WithAuth from 'components/withAuth';
import Preloader from 'components/preloader';
import PageTop from 'components/_layout/pageTop';
import InitiativeRow from 'components/initiativeRow';
import SearchAndFilter from 'components/searchAndFilter';
import ListCount from 'components/_layout/listCount';
import TutorialBar from 'components/tutorialBar';

const HomeComponent = () => {
    // ///////////////////
    // STORES
    // ///////////////////

    const { reset } = useInitiativeDataStore();

    // ///////////////////
    // HOOKS
    // ///////////////////

    const { label, pickList, tagLabel } = useLabels();
    const { ewGet, ewGetWithPagination } = useElseware();
    const { enableAction } = usePermissions();
    const { getUserAccountType, getUserAccountId } = useUser();
    const { initial, filtered } = useSearchAndFilter({
        instanceName: 'initiatives',
    });

    // ///////////////////
    // STATE
    // ///////////////////

    const [tagsPath, setTagsPath] = useState(null);
    const [funders, setFunders] = useState(null);

    // ///////////////////
    // DATA
    // ///////////////////

    const { data: initiativesData } = ewGetWithPagination(
        'initiative/initiatives-overview',
        true,
        true
    );
    const { data: tagsData } = ewGet(
        tagsPath,
        {
            funders,
        },
        funders && tagsPath
    );

    const readyToShow = initiativesData && tagsData;
    const hasContent =
        initial?.length > 0 &&
        tagsData?.data &&
        Object.values(tagsData?.data).length > 0;

    const dataForSearchAndFilter = Array.from(
        new Set(initiativesData?.data?.map(a => a.Id))
    )
        .map(id => {
            return initiativesData?.data?.find(a => a.Id === id);
        })
        .map(initiative => ({
            __filtering: {
                text: [
                    initiative.Name,
                    ...initiative._applicationIds,
                    ...initiative._fundingRecipients?.map(
                        fr => fr?.Account__r?.Name
                    ),
                ],
                funder: initiative._funders.map(x => ({
                    label: x.Account__r?.Name,
                    value: x.Account__c,
                })),
                tag: initiative._initiativeTags?.map(x => x.Tag__c),
                category: [initiative.Category__c],
                startDate: [initiative.Grant_Start_Date__c],
                endDate: [initiative.Grant_End_Date__c],
                initiativeType: [initiative.Configuration_Type__c],
            },
            ...initiative,
        }));

    // ///////////////////
    // EFFECTS
    // ///////////////////

    useEffect(() => {
        if (initiativesData?.data) {
            const userAccountType = getUserAccountType();
            if (userAccountType === ACCOUNTS.super) {
                setTagsPath('tag/all-tags');
                setFunders([]);
            }
            if (userAccountType === ACCOUNTS.funder) {
                setTagsPath('tag/all-funder-tags-multiple');
                setFunders([getUserAccountId()]);
            }
            if (userAccountType === ACCOUNTS.grantee) {
                setTagsPath('tag/all-funder-tags-multiple');
                setFunders(
                    Array.from(
                        new Set([
                            ...initiativesData?.data
                                ?.map(item =>
                                    item._funders.map(x => x.Account__c)
                                )
                                .flat(),
                        ])
                    )
                );
            }
        }
    }, [getUserAccountType(), initiativesData]);

    useEffect(() => {
        reset();
    }, []);

    // ///////////////////
    // RENDER
    // ///////////////////

    return (
        <>
            <div className="flex flex-col mb-48 animate-fade-in">
                <PageTop
                    {...{
                        heading: label('InitiativeManagerHeading'),
                        methods: {
                            primary: {
                                action: enableAction(
                                    getPermissionRules(
                                        'create',
                                        'nameAndCategory',
                                        'add'
                                    ),
                                    '/create/create-start'
                                ),
                                label: label('InitiativeManagerCreate'),
                            },
                        },
                    }}
                />
                <TutorialBar
                    granteeTutorialKey="InitiativeSearch"
                    theme="blue"
                />
                {readyToShow && initiativesData?.data?.length > 0 && (
                    <SearchAndFilter
                        {...{
                            data: dataForSearchAndFilter,
                            instanceName: 'initiatives',
                            allTags: Object.values(tagsData?.data),
                            filters: {
                                funder: true,
                                tag: true,
                                category: true,
                                startDate: true,
                                endDate: true,
                                initiativeType: true,
                            },
                        }}
                    />
                )}
            </div>

            {hasContent ? (
                <>
                    <ListCount
                        count={`${filtered?.length} / ${
                            initial?.length
                        } ${label('InitiativeManagerHeading')} ${label(
                            'ListItemsFound'
                        )}`}
                    />
                    <div className="flex flex-col pb-64 space-y-12">
                        {/* Initiatives and Results are there */}
                        {hasContent > 0 &&
                            filtered.length > 0 &&
                            filtered?.map(item => (
                                <div key={item.Id} className=" animate-fade-in">
                                    <InitiativeRowWithContent {...{ item }} />
                                </div>
                            ))}

                        {/* Initiatives are there but no results */}
                        {hasContent > 0 && filtered?.length < 1 && (
                            <EmptyState
                                {...{
                                    heading: label(
                                        'InitiativesOverviewNoResultsHeading'
                                    ),
                                    body: label(
                                        'InitiativesOverviewNoResultsBody'
                                    ),
                                }}
                            />
                        )}
                    </div>
                </>
            ) : (
                <>
                    {/* Loading / No initiatives */}
                    {readyToShow && !hasContent ? (
                        <EmptyState
                            {...{
                                heading: label(
                                    'InitiativesOverviewNoInitiativesHeading'
                                ),
                                body: label(
                                    'InitiativesOverviewNoInitiativesBody'
                                ),
                            }}
                        />
                    ) : (
                        <Preloader />
                    )}
                </>
            )}
        </>
    );
};

const InitiativeRowWithContent = ({ item }) => {
    // ///////////////////
    // HOOKS
    // ///////////////////

    const { getUserAccountType, getUserAccountId } = useUser();

    // ///////////////////
    // STATE
    // ///////////////////

    const [reports, setReports] = useState(item?._reports);

    // ///////////////////
    // DATA
    // ///////////////////

    const hasAccess = item?.UserRecordAccess?.HasReadAccess ?? false;

    // If funder, then only get reports where funder has same Initiative_Funding__c = Report.Funding_Report__c
    useEffect(() => {
        if (getUserAccountType() === ACCOUNTS.funder) {
            const currentFunderId = getUserAccountId();
            const fundersFundings = item?._funders
                .filter(funder => funder.Account__c === currentFunderId)
                .map(funder => funder.Initiative_Funding__c);
            const reportsFromFunder = item?._reports.filter(report =>
                fundersFundings.includes(report.Funding_Report__c)
            );
            setReports(reportsFromFunder);
        } else {
            setReports(item?._reports);
        }
    }, [item, getUserAccountType()]);

    // ///////////////////
    // RENDER
    // ///////////////////

    return (
        <InitiativeRow
            {...{
                item,
                reports,
                fundingRecipients: item?._fundingRecipients,
                funders: item?._funders,
                link: hasAccess ? `/${item?.Id}/overview` : null,
                limitedAccess: !hasAccess,
            }}
        />
    );
};

const EmptyState = ({ heading, body }) => {
    // ///////////////////
    // RENDER
    // ///////////////////

    return (
        <div className="flex flex-col items-center justify-center space-y-32 animate-fade-in !pt-48">
            <div className="relative h-[192px] w-full">
                <Image
                    layout="fill"
                    objectFit="contain"
                    src="https://www.datocms-assets.com/69268/1665992793-initiatives-reports-empty-state.png"
                />
            </div>
            <div className="flex flex-col space-y-16 text-center">
                <p className="text-blue-100 t-h6">{heading}</p>
                <p className="text-blue-100 t-caption">{body}</p>
            </div>
        </div>
    );
};

HomeComponent.propTypes = {
    pageProps: t.object,
};

HomeComponent.defaultProps = {
    pageProps: {},
};

HomeComponent.layout = 'default';

export default WithAuth(HomeComponent);
